import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { commitTransfer } from 'src/services/DataVaultTransfer.service';
import { setAnswersLoaded } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { RENTER_PATHS } from 'src/routes/renterPaths';

interface UseCommitTransferResult {
  /**
   * Resolves true when the renter was moved on to the interview, false when
   * the commit failed and they must stay on the review screen.
   *
   * Navigating after a failed commit is not harmless: InterviewRoute runs the
   * transfer guard on mount, sees the transfer is still pending, and sends the
   * renter straight back here. That reads as a button that does nothing.
   */
  startCommit: () => Promise<boolean>;
}

function isAlreadyCommitted(err: unknown): boolean {
  return (
    (err as { response?: { status?: number } } | null)?.response?.status === 409
  );
}

/**
 * Fire-and-forget DVT commit hook.
 *
 * memberId MUST come from the DVT page's URL params (useParams), NOT from
 * state.interview.onBehalfOf. The interview state is reset when InterviewRoute
 * unmounts during the transfer-guard redirect, so Redux is unreliable here.
 * Passing a stale Redux value could route a helper into the wrong member's
 * interview context — a confidentiality risk.
 */
export function useCommitTransfer(
  transferId: number | null,
  surveyId: number | undefined,
  certId: number | undefined,
  memberId: number | null
): UseCommitTransferResult {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const startCommit = useCallback(async (): Promise<boolean> => {
    if (!transferId) {
      return false;
    }

    try {
      await commitTransfer(transferId);
    } catch (err) {
      // 409 means the transfer was already committed (e.g. a double click or
      // the Celery task beat us to it) — the renter is safe to move on.
      // Anything else means the commit was not enqueued; stay put so the
      // caller can show the failure instead of bouncing off the guard.
      if (!isAlreadyCommitted(err)) {
        return false;
      }
    }

    // Invalidate cached answers so the interview fetches fresh committed data.
    if (certId) {
      dispatch(setAnswersLoaded({ certId, loaded: false }));
    }

    // Build the interview URL from the URL-param memberId, not from Redux.
    // For proxy mode memberId is the HouseholdMember.id, which matches the
    // /members/:memberId segment consumed by InterviewRoute.
    const interviewPath = memberId
      ? `/${RENTER_PATHS.HOME}/interview/${surveyId}/members/${memberId}`
      : `/${RENTER_PATHS.HOME}/interview/${surveyId}`;

    navigate(interviewPath, { replace: true });
    return true;
  }, [transferId, surveyId, certId, memberId, dispatch, navigate]);

  return { startCommit };
}
